//======================================================================================================
//
// This store keeps track of all the events that fire throughout the application
//
//======================================================================================================

import { defineStore } from "pinia";
import { useToastStore } from "./toastStore";
import { useImportStore } from "./importStore";
import { EventSourcePolyfill } from "event-source-polyfill";
import { useConfigStore as configStore } from "@/stores/configStore";
import { config } from "@/types/config";
// import { DatasetStatus } from "@/types/dataset";
import { useHierarchyStore } from "./hierarchyStore";

const EventSource = EventSourcePolyfill;

export const useEventStore = defineStore("eventStore", {
	state: () => ({
		initialized: false as boolean,
		config: null as config | null,
		toast: null as any,
		eventSource: null as any,
		latestUploadedDatasetId: "",
		latestUploadedDatasetFolderPath: "",
		latestUploadedDatasetType: "",
		latestUploadedDatasetGroupName: ""
	}),
	getters: {
		getLatestUploadedDatasetId(): string {
			return this.latestUploadedDatasetId;
		},
		getLatestUploadedDatasetFolderPath(): string {
			return this.latestUploadedDatasetFolderPath;
		},
		getLatestUploadedDatasetType(): string {
			return this.latestUploadedDatasetType;
		},
		getLatestUploadedDatasetGroupName(): string {
			return this.latestUploadedDatasetGroupName;
		}
	},
	actions: {
		async init() {
			if (this.initialized) {
				return;
			}
			await configStore().init();
			this.config = configStore().config;
			this.initialized = true;
			const token =
				localStorage.getItem("mineportal-auth-token") ||
				localStorage.getItem("mineportal-oauth-token");
			this.toast = useToastStore();
			this.eventSource = new EventSource(`${this.config?.endpoints.api}/event`, {
				headers: {
					Authorization: `Bearer ${token}`
				}
			});
			const currentSite = localStorage.getItem("mineportal-active-site");

			const generateHandler = (event: any, eventType: string) => {
				const eventData = JSON.parse(event.data);

				this.latestUploadedDatasetId = eventData.datasetId;
				this.latestUploadedDatasetFolderPath = eventData.datasetFolderPath;
				this.latestUploadedDatasetType = eventData.datasetType;
				this.latestUploadedDatasetGroupName = eventData.datasetGroupName;

				if (eventData.title == "Rawfile Upload Result: Complete") {
					if (useImportStore().finalizingUploads[eventData.datasetId])
						useImportStore().finalizingUploads[eventData.datasetId] = "Complete";
				}

				if (
					eventData.title === "Dataset Generation Result" &&
					eventData.message.includes("The Drillhole dataset [BLASTDOG]")
				) {
					useImportStore().setIsBlastDogDatasetInProgress(false);
				}

				if (
					eventData.title === "Dataset Generation" &&
					eventData.message.includes("Dataset Generation Initiated [BLASTDOG]")
				) {
					useImportStore().setIsBlastDogDatasetInProgress(true);
				}

				if (eventData.datasetSiteId && eventData.datasetSiteId != currentSite) {
					return;
				}

				this.toast.toast({
					title: eventData.title,
					dismissable: false,
					message: eventData.message,
					type: eventType
				});
			};

			const generateDataGenerationHandler = () => {
				// const eventData = JSON.parse(event.data);
				// const { datasetId } = eventData;
				// this.datasetStatuses[datasetId] = status;
				// we can't just update the status, we need the updated metadata, hierarchies, etc.
				useHierarchyStore().fetchHierarchy();
			};

			// this.eventSource.onopen = () => console.log("connected to event source");

			this.eventSource.onmessage = (event: any) => {
				generateHandler(event, "primary");
			};

			this.eventSource.addEventListener("success", (event: any) => {
				generateHandler(event, "success");
			});

			this.eventSource.addEventListener("warning", (event: any) => {
				generateHandler(event, "warning");
			});

			this.eventSource.addEventListener("errorEvent", (event: any) => {
				generateHandler(event, "error");
			});

			this.eventSource.addEventListener("primary", (event: any) => {
				generateHandler(event, "primary");
			});

			this.eventSource.addEventListener("generate", (event: any) => {
				generateHandler(event, "success");
				generateDataGenerationHandler();
			});

			this.eventSource.addEventListener("complete", (event: any) => {
				generateHandler(event, "success");
				generateDataGenerationHandler();
			});

			this.eventSource.addEventListener("failedGenerate", (event: any) => {
				generateHandler(event, "error");
				generateDataGenerationHandler();
			});
		},
		clearLatestDatasetType() {
			this.latestUploadedDatasetType = "";
		},
		clearLatestDatasetId() {
			this.latestUploadedDatasetId = "";
		},
		close() {
			this.eventSource.close();
		}
	}
});
